import React from "react"
import type { UVInsertFaceElementTextListConfig } from "@shared/models/uv-inserts"
import styles from "./visualiser-text-list.module.css"
import { getVisualiserTextListViewModel } from "./VisualiserTextList.vm"

type VisualiserTextListProps = {
  element: UVInsertFaceElementTextListConfig
  /** The host-populated text; only this element's month/year/moment keys are read. */
  textByKey: Record<string, string>
}

/**
 * Read-only list of "★ Month Year / moment" rows on a UV insert face.
 *
 * The customer never types into this element — the host derives the months from the stone slots and
 * writes the year and moment it collects beside the face into `textByKey` — so there is no input and
 * no focus handling here.
 */
export const VisualiserTextList = ({ element, textByKey }: VisualiserTextListProps) => {
  const vm = getVisualiserTextListViewModel({ element, textByKey })

  return (
    <ul
      role="list"
      aria-label={vm.ariaLabel}
      className={[
        styles["visualiser-text-list"],
        vm.align === "right" ? styles["visualiser-text-list--right"] : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={vm.listStyle}
    >
      {vm.rows.map((row) => (
        <li key={row.index} className={styles["visualiser-text-list__row"]} style={vm.rowStyle}>
          {vm.star ? (
            <span className={styles["visualiser-text-list__lines"]}>
              <span
                className={styles["visualiser-text-list__headline-line"]}
                style={{ gap: `${vm.star.gap}px`, minHeight: `${vm.star.lineHeightPx}px` }}
              >
                <svg
                  className={styles["visualiser-text-list__star"]}
                  width={vm.star.size}
                  height={vm.star.size}
                  viewBox={`0 0 ${vm.star.size} ${vm.star.size}`}
                  overflow="visible"
                  aria-hidden
                >
                  <polygon
                    points={vm.star.points}
                    fill={vm.star.fillColor}
                    stroke={vm.star.outlineColor}
                    strokeWidth={vm.star.outlineWidth}
                    strokeLinejoin="round"
                  />
                  <circle
                    cx={vm.star.size / 2}
                    cy={vm.star.size / 2}
                    r={vm.star.circleRadius}
                    fill={row.bulletColor}
                  />
                </svg>
                <span className={styles["visualiser-text-list__headline"]}>{row.headline}</span>
              </span>
              {row.moment ? (
                <span className={styles["visualiser-text-list__moment"]} style={vm.momentStyle}>
                  {row.moment}
                </span>
              ) : null}
            </span>
          ) : (
            <>
              {vm.showBullet ? (
                <span
                  className={styles["visualiser-text-list__bullet"]}
                  style={{ color: row.bulletColor }}
                  aria-hidden
                >
                  {vm.bulletGlyph}
                </span>
              ) : null}
              <span className={styles["visualiser-text-list__lines"]}>
                <span className={styles["visualiser-text-list__headline"]}>{row.headline}</span>
                {row.moment ? (
                  <span className={styles["visualiser-text-list__moment"]} style={vm.momentStyle}>
                    {row.moment}
                  </span>
                ) : null}
              </span>
            </>
          )}
        </li>
      ))}
      {/*
        Decorative only: `aria-hidden` keeps the rule out of the accessible tree, so `vm.ariaLabel`
        and the rows above are the whole of what a screen reader hears. It is an `<li>` rather than
        a `<span>` purely so the `<ul>` stays valid HTML; being absolutely positioned it takes no
        part in the flex layout or the row gap.
      */}
      {vm.rule ? (
        <li
          className={styles["visualiser-text-list__rule"]}
          style={vm.rule.containerStyle}
          aria-hidden
        >
          <span className={styles["visualiser-text-list__rule-line"]} style={vm.rule.lineStyle} />
          {vm.rule.dotStyle ? (
            <>
              <span
                className={[
                  styles["visualiser-text-list__rule-dot"],
                  styles["visualiser-text-list__rule-dot--start"],
                ].join(" ")}
                style={vm.rule.dotStyle}
              />
              <span
                className={[
                  styles["visualiser-text-list__rule-dot"],
                  styles["visualiser-text-list__rule-dot--end"],
                ].join(" ")}
                style={vm.rule.dotStyle}
              />
            </>
          ) : null}
        </li>
      ) : null}
    </ul>
  )
}
